import { callAPI } from "@hooks/useApi";
import { useCallback, useEffect, useRef, useState } from "react";

const useUserProfile = (sub, token) => {
	const [data, setData] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const hasFetched = useRef(false);
	const isFetching = useRef(false);
	const shouldFetch = Boolean(token && sub);
	const fetch = useCallback(
		async (override = false) => {
			if (
				!token ||
				!sub ||
				isFetching.current ||
				(override ? false : hasFetched.current)
			) {
				return;
			}

			isFetching.current = true;
			setIsLoading(true);
			try {
				const response = await callAPI(
					"/users",
					{
						query: {
							auth0: `eq.${sub}`,
							select: [
								"*",
								"permissions:permissions(*)",
								"roles:users_organisations(*, organisation:organisations(id, name, broker_id, paris_id))",
							],
						},
						one: true,
					},
					token
				);
				hasFetched.current = true;
				setData(response);
				setError(null);
			} catch (error) {
				if (error.message === "BANNED") {
					hasFetched.current = true;
				}
				setError(error);
			} finally {
				isFetching.current = false;
				setIsLoading(false);
			}
		},
		[sub, token]
	);

	useEffect(() => {
		hasFetched.current = false;
		isFetching.current = false;
		setData(null);
		setError(null);
		setIsLoading(false);
	}, [shouldFetch, sub, token]);

	useEffect(() => {
		fetch();
	}, [fetch]);

	return {
		profile: data,
		isLoading: (isLoading || (shouldFetch && data === null)) && !error,
		error,
		mutate: setData,
		refetch: () => {
			hasFetched.current = false;
			setData(null);
			fetch(true);
		},
	};
};

export default useUserProfile;
